// ** React Imports
import { useState, useEffect, useRef } from "react";

// ** Redux & Store & Actions
import { useSelector, useDispatch } from "react-redux";
import { updateStatisticsSelfMessages } from "store/modules/global";

// ** Custom Components
import ConversationCloudflare from "./components/ConversationCloudflare";
import ChattingWith from "./components/ConversationChat/components/ChattingWith";
import Block from "components/layout/Block";

// ** Custom Hooks
import useOpenClose from "hooks/useOpenClose";
import { useWebsocket } from "./hooks/useWebsocket";

// ** Utils & Helpers
import { markAsReaded } from "./helpers/messagesApiActions";

// ** Contexts
import { ConversationProvider } from "./helpers/ConversationContext";

// #####################################################

const ShowMsg = ({
	match: {
		params: { id: conversationId },
	},
}) => {
	const dispatch = useDispatch();
	const loggedUser = useSelector((state) => state.globalUser);
	const statisticsSelf = useSelector((state) => state.global.statisticsSelf);

	const enableDebug =
		loggedUser.login === "sftester2" || loggedUser.login === "sftester";

	const prevMessagesCount = useRef(null);
	const firstRenderRef = useRef(true);
	const prevProfileStatusRef = useRef(null);
	const isFirstRender = useRef(true);

	const [isWindowActive, setIsWindowActive] = useState(true);
	const [conversation, setConversation] = useState({
		websocketUrl: null,
		message_hash: null,
		messages: [],
		pagination: [null, null, null],
		user: {
			token: null,
			id: null,
			login: null,
			avatar: null,
			private_data: false,
			profile_status: null,
			status_vip: null,
			online: null,
			editor: { disabled: false, info: null },
		},
		video: false,
		loading: false,
		forceRefresh: false,
	});
	const [videoChat, setVideoChat] = useState({
		chatPartnerStreamStatus: "initial",
		chatPartnerStreamPicture: null,
		chatPartnerAskingAboutStream: false,
		publisherHash: null,
		publisherPicture: null,
		watcherHash: null,
		secondDevicePublisherPicture: null,
	});

	const [
		{ chatPartnerOnlineStatus, chatPartnerProfileStatus },
		setWebsocketInfo,
	] = useState({
		chatPartnerOnlineStatus: null,
		chatPartnerProfileStatus: null,
	});

	const {
		isOpen: isOpenVideoChat,
		open: openVideoChat,
		close: closeVideoChat,
	} = useOpenClose();

	const generateSessionID = () => {
		const timestamp = Date.now();
		const randomPart = Math.floor(Math.random() * 1000000);
		return `${timestamp}-${randomPart}`;
	};
	const sessionIdRef = useRef(generateSessionID());

	const { connectionStatus, websocket, pingMessagesCount } = useWebsocket({
		conversationId,
		conversation,
		enableDebug,
		loggedUser,
		setVideoChat,
		setConversation,
		setWebsocketInfo,
		sessionId: sessionIdRef.current,
		closeVideoChat,
	});

	const sendWebsocketMessage = (data) => {
		if (websocket?.readyState === 1) {
			websocket.send(
				JSON.stringify({
					publisherId: loggedUser.id,
					recipientId: conversation.user.id,
					conversationId,
					...data,
				})
			);
		}
	};

	useEffect(() => {
		const lastMessage =
			conversation?.messages[conversation.messages.length - 1];

		if (
			lastMessage &&
			!lastMessage.read &&
			!lastMessage.send &&
			websocket?.readyState === 1
		) {
			sendWebsocketMessage({
				action: "read_messages",
			});
		}
	}, [websocket?.readyState]);

	useEffect(() => {
		if (firstRenderRef.current) {
			prevProfileStatusRef.current = loggedUser.profileStatus;
			firstRenderRef.current = false;
			return;
		}

		if (loggedUser.profileStatus !== prevProfileStatusRef.current) {
			sendWebsocketMessage({
				action: "profile_status",
				profileStatus: loggedUser.profileStatus,
				onlineStatus: loggedUser.online,
			});

			prevProfileStatusRef.current = loggedUser.profileStatus;
		}
	}, [loggedUser.profileStatus]);

	useEffect(() => {
		const handleFocus = () => {
			setIsWindowActive(true);
		};

		const handleBlur = () => {
			setIsWindowActive(false);
		};

		window.addEventListener("focus", handleFocus);
		window.addEventListener("blur", handleBlur);

		return () => {
			window.removeEventListener("focus", handleFocus);
			window.removeEventListener("blur", handleBlur);
		};
	}, []);

	useEffect(() => {
		if (prevMessagesCount.current === conversation.messages.length) {
			return;
		}

		const lastMessage =
			conversation.messages[conversation.messages.length - 1];

		if (
			lastMessage?.send === false &&
			lastMessage?.read === false &&
			isWindowActive
		) {
			prevMessagesCount.current = conversation.messages.length;
			markAsReaded(conversationId);
			sendWebsocketMessage({
				action: "read_messages",
			});
			if (isFirstRender.current) {
				isFirstRender.current = false;
				setTimeout(() => {
					dispatch(
						updateStatisticsSelfMessages(
							statisticsSelf.messages > 0
								? statisticsSelf.messages - 1
								: 0
						)
					);
				}, 3000);
			}
		}
	}, [
		conversation.messages.length,
		isWindowActive,
		prevMessagesCount.current,
	]);

	// #####################################################

	return (
		<>
			{enableDebug && (
				<div
					style={{
						border:
							connectionStatus === "Open"
								? "3px solid green"
								: "3px solid red",
					}}
				>
					The WebSocket is currently - {connectionStatus}
				</div>
			)}
			<Block flex>
				{conversation.user.login && (
					<ChattingWith
						conversationUser={conversation.user}
						chatPartnerOnlineStatus={chatPartnerOnlineStatus}
						chatPartnerProfileStatus={chatPartnerProfileStatus}
					/>
				)}
			</Block>

			<ConversationProvider
				value={{
					chatPartnerOnlineStatus,
					chatPartnerProfileStatus,
					chatPartnerStreamPicture:
						videoChat.chatPartnerStreamPicture,
					showAcceptVideoChatButton: videoChat.watcherHash,
					publisherPicture: videoChat.publisherPicture,
					isOpenVideoChat,
					secondDevicePublisherPicture:
						videoChat.secondDevicePublisherPicture,
				}}
			>
				<ConversationCloudflare
					conversation={conversation}
					setConversation={setConversation}
					videoChat={videoChat}
					setVideoChat={setVideoChat}
					isOpenVideoChat={isOpenVideoChat}
					openVideoChat={openVideoChat}
					closeVideoChat={closeVideoChat}
					sendWebsocketMessage={sendWebsocketMessage}
					loggedUser={loggedUser}
					conversationId={conversationId}
					websocketHasMessages={pingMessagesCount > 0}
					setWebsocketInfo={setWebsocketInfo}
					sessionId={sessionIdRef.current}
				/>
			</ConversationProvider>
		</>
	);
};

export default ShowMsg;
